import {
  type DraftLineInput,
  type InvoiceDetail,
  type UpdateDraftInvoiceData,
} from '@/hooks/useInvoices';
import {
  dueDateIsoFromPaymentTermsDays,
  pickDefaultPaymentTermsDays,
} from '@/components/workorders/billing/paymentSplit';
import {
  emptyEditorLine,
  editorLineToDraft,
  invoiceLineToEditor,
  parseEditorDecimal,
  type InvoiceEditorLine,
} from './invoiceEditorLines';

const MS_PER_DAY = 24 * 60 * 60 * 1000;

export function emptyToNull(value: string): string | null {
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}

/** Date input value (YYYY-MM-DD) from an invoice due-date ISO string. */
export function dueDateIsoToDateInput(iso: string | null | undefined): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
}

/**
 * API due dates are offset datetimes. Noon UTC keeps the calendar day stable
 * across Finnish winter/summer time when the user picked a date-only field.
 */
export function dateInputToDueDateIso(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const date = new Date(`${trimmed}T12:00:00.000Z`);
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString();
}

export function paymentTermsDaysFromDates(
  issueDateIso: string,
  dueDateIso: string,
  options: readonly number[],
): number | null {
  const issue = new Date(issueDateIso).getTime();
  const due = new Date(dueDateIso).getTime();
  if (!Number.isFinite(issue) || !Number.isFinite(due)) return null;
  const days = Math.round((due - issue) / MS_PER_DAY);
  return options.includes(days) ? days : null;
}

export function defaultDueDateInput(
  issueDateIso: string,
  paymentTermsDays: number,
): string {
  return dueDateIsoToDateInput(dueDateIsoFromPaymentTermsDays(paymentTermsDays, new Date(issueDateIso)));
}

export function resolveDraftPaymentTermsDays(
  invoice: Pick<InvoiceDetail, 'issueDate' | 'dueDate'>,
  options: readonly number[],
): number {
  if (invoice.dueDate) {
    const matched = paymentTermsDaysFromDates(invoice.issueDate, invoice.dueDate, options);
    if (matched !== null) return matched;
  }
  return pickDefaultPaymentTermsDays(options);
}

export function editorLinesFromInvoice(
  invoice: Pick<InvoiceDetail, 'lineItems' | 'taxPercent'>,
): InvoiceEditorLine[] {
  const items = invoice.lineItems.filter((line) => line.lineType !== 'tax');
  if (items.length === 0) {
    return [emptyEditorLine(invoice.taxPercent, 'new-0')];
  }
  return items.map((line, index) =>
    invoiceLineToEditor(line, invoice.taxPercent, line.id || `line-${index}`),
  );
}

export type DraftEditorValidationKey =
  | 'edit.dueDateRequired'
  | 'edit.lineDescriptionRequired'
  | 'edit.positiveQuantityRequired'
  | 'edit.linesRequired';

export function draftEditorValidationKey(
  lines: readonly InvoiceEditorLine[],
  dueDate: string,
): DraftEditorValidationKey | null {
  if (lines.length === 0) return 'edit.linesRequired';
  if (!dueDate.trim()) return 'edit.dueDateRequired';
  if (lines.some((line) => !line.description.trim())) return 'edit.lineDescriptionRequired';
  if (lines.some((line) => parseEditorDecimal(line.quantity) <= 0)) {
    return 'edit.positiveQuantityRequired';
  }
  return null;
}

/**
 * Finalize guard from the saved invoice alone. Invoice content is immutable
 * since #1960, so there is no editor state to consult (#3876).
 */
export function savedDraftValidationKey(
  invoice: Pick<InvoiceDetail, 'lineItems' | 'dueDate'>,
): DraftEditorValidationKey | null {
  const items = invoice.lineItems.filter((line) => line.lineType !== 'tax');
  if (items.length === 0) return 'edit.linesRequired';
  if (!invoice.dueDate) return 'edit.dueDateRequired';
  if (items.some((line) => !line.description.trim())) return 'edit.lineDescriptionRequired';
  if (items.some((line) => line.quantity <= 0)) return 'edit.positiveQuantityRequired';
  return null;
}

/**
 * Client-side mirror of finalize-draft-invoice.ts missing-field checks so
 * the finalize button cannot fire with data the API will reject (#2150).
 */
export function missingDraftFinalizationFields(input: {
  recipientName: string | null | undefined;
  recipientEmail: string | null | undefined;
  sellerIban: string | null | undefined;
  totalCents: number;
  requireEmail: boolean;
}): string[] {
  const missing: string[] = [];
  if (!input.recipientName?.trim()) missing.push('recipientName');
  if (input.requireEmail && !input.recipientEmail?.trim()) missing.push('recipientEmail');
  if (!input.sellerIban?.trim()) missing.push('sellerIban');
  if (input.totalCents <= 0) missing.push('positiveTotal');
  return missing;
}

export function draftLinesForSave(lines: readonly InvoiceEditorLine[]): DraftLineInput[] {
  return lines.map(editorLineToDraft);
}

export function buildUpdateDraftPayload(
  invoice: InvoiceDetail,
  lines: readonly InvoiceEditorLine[],
  fields: {
    dueDate: string;
    notes: string;
    termsAndConditions: string;
    customerReference: string;
    paymentReferenceNumber: string;
  },
): UpdateDraftInvoiceData {
  return {
    invoiceId: invoice.id,
    ...(invoice.bookingId ? { workOrderId: invoice.bookingId } : {}),
    expectedVersion: invoice.version,
    lines: draftLinesForSave(lines),
    discountCents: invoice.discountCents,
    discountReason: invoice.discountReason,
    dueDate: dateInputToDueDateIso(fields.dueDate),
    notes: emptyToNull(fields.notes),
    termsAndConditions: emptyToNull(fields.termsAndConditions),
    customerReference: emptyToNull(fields.customerReference),
    paymentReferenceNumber: emptyToNull(fields.paymentReferenceNumber),
  };
}

export function sellerIbanFromSettings(settings: { bankIban?: unknown } | null | undefined): string {
  return typeof settings?.bankIban === 'string' ? settings.bankIban : '';
}
